import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics, categories, statusLabels } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const [comic, setComic] = useState<Comic | null>(null);
  const [relatedComics, setRelatedComics] = useState<Comic[]>([]);
  const [expandedChapter, setExpandedChapter] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const found = comics.find((c) => c.id === Number(id));
    setComic(found || null);

    if (found) {
      const related = comics
        .filter((c) => c.category === found.category && c.id !== found.id)
        .slice(0, 4);
      setRelatedComics(related);
    }
  }, [id]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-800 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">您要查看的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === comic.category);
  const statusClass = comic.status === 'serializing'
    ? 'bg-accent-100 text-accent-700'
    : 'bg-secondary-100 text-secondary-700';

  const visibleChapters = expandedChapter ? comic.chapters : comic.chapters.slice(0, 5);

  const articleSchema = {
    headline: `${comic.title} - ${comic.latestChapter}`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2025-01-01',
    dateModified: comic.updateTime,
    image: comic.coverImage,
    publisher: { '@type': 'Organization', name: 'UU漫画' },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            ...articleSchema,
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '分类浏览', href: '/category/rexue' },
              { label: category?.name || comic.category, href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          {/* Comic Info Header */}
          <article className="w-full px-4 md:px-8 py-8 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                <div className="flex-shrink-0 w-40 md:w-48 mx-auto md:mx-0">
                  <div className="relative overflow-hidden rounded-lg aspect-[5/7]">
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title} - UU漫画`}
                      title={`${comic.title} - ${comic.author} - UU漫画`}
                      className="w-full h-full object-cover object-top"
                    />
                    <span className={`absolute top-2 right-2 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${statusClass}`}>
                      {statusLabels[comic.status]}
                    </span>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
                    {comic.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-600 mb-4">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line text-foreground-400"></i> {comic.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-folder-line text-foreground-400"></i> {category?.name || comic.category}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line text-foreground-400"></i> 更新于 {comic.updateTime}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line text-foreground-400"></i> {(comic.views / 10000).toFixed(0)}万 阅读
                    </span>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    {comic.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <Link
                      to={`/article/${comic.id}`}
                      className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      开始阅读
                    </Link>
                    <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-accent-600 hover:border-accent-300 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-heart-line text-base"></i>
                    </button>
                    <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-share-forward-line text-base"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </article>

          {/* Chapter List */}
          <section className="w-full px-4 md:px-8 py-10">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl text-foreground-950">
                  章节目录
                </h2>
                <button
                  onClick={() => setExpandedChapter(!expandedChapter)}
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  {expandedChapter ? '收起' : '查看全部章节'}
                  <i className={`ml-1 text-xs ${expandedChapter ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {visibleChapters.map((chapter) => (
                  <Link
                    key={chapter.id}
                    to={`/article/${comic.id}`}
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer group"
                  >
                    <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors truncate">
                      {chapter.title}
                    </span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap ml-2">
                      {chapter.updateTime}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Content */}
          <section className="w-full px-4 md:px-8 py-10 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-xl text-foreground-950 mb-5">内容简介</h2>
              <div
                className="prose prose-sm max-w-none text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: comic.content }}
              />
            </div>
          </section>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section className="w-full px-4 md:px-8 py-14 bg-background-50">
              <div className="max-w-7xl mx-auto">
                <div className="flex items-end justify-between mb-6">
                  <div>
                    <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
                      相关推荐
                    </h2>
                    <p className="text-sm text-foreground-500 mt-1">同类型漫画推荐</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                  {relatedComics.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.id}`}
                      className="group block cursor-pointer"
                    >
                      <div className="relative overflow-hidden rounded-lg aspect-[5/7] bg-background-100">
                        <img
                          src={related.coverImage}
                          alt={related.title}
                          title={`${related.title} - UU漫画`}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                        <span className={`absolute top-2 right-2 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
                          related.status === 'serializing'
                            ? 'bg-accent-100 text-accent-700'
                            : 'bg-secondary-100 text-secondary-700'
                        }`}>
                          {statusLabels[related.status]}
                        </span>
                      </div>
                      <div className="mt-2.5">
                        <h3 className="text-sm font-semibold text-foreground-900 leading-tight line-clamp-1 group-hover:text-primary-500 transition-colors">
                          {related.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-1">{related.author}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}